import { useParams } from 'react-router-dom';
import {
  useGetFreshdeskEmailAgentsQuery,
  useGetFreshdeskEmailGroupsQuery,
  useGetProjectByIdQuery,
} from 'src/services';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useAppSelector } from 'src/store';
import { ProductsTypeBack } from 'src/constants/products';
import { InputInfoToolTip } from 'src/components/Input/InputInfoToolTip';
import { AgentsSelect } from 'src/components/Integrations/IntegrationForms/IntegrationFormFields/AgentsSelect/AgentsSelect';
import { EscalationGroupSelect } from 'src/components/Integrations/IntegrationForms/IntegrationFormFields/EscalationGroupSelect/EscalationGroupSelect';
import type { SelectedIntegration } from 'src/store/slices/integrationSlice';
import type { FreshdeskAgent, FreshdeskGroup } from 'src/services/projectIntegrationsService';
import { useLoaderWhile } from 'src/hooks/useLoaderWhile';

const InitialAgents: FreshdeskAgent[] = [];
const InitialGroups: FreshdeskGroup[] = [];

export const EditFreshdeskEmailAutoSetupSettings = ({
  integration,
}: {
  integration?: SelectedIntegration;
}) => {
  const { id } = useParams();
  const { data: currentProject } = useGetProjectByIdQuery({ id: id });
  const { selectedIntegration } = useAppSelector((s) => s.integrationSlices);
  const [agents, setAgents] = useState(InitialAgents);
  const [groups, setGroups] = useState(InitialGroups);
  const {
    data: agentsResponse,
    isLoading: isLoadingAgents,
    isError: isErrorAgents,
  } = useGetFreshdeskEmailAgentsQuery(
    {
      projectId: id,
      integrationId: selectedIntegration?.id,
      type: ProductsTypeBack[currentProject?.project_type],
    },
    { skip: !selectedIntegration?.id || !currentProject },
  );

  const {
    data: groupsResponse,
    isLoading: isLoadingGroups,
    isError: isErrorGroups,
  } = useGetFreshdeskEmailGroupsQuery(
    {
      projectId: id,
      integrationId: selectedIntegration?.id,
      type: ProductsTypeBack[currentProject?.project_type],
    },
    { skip: !selectedIntegration?.id || !currentProject },
  );

  useEffect(() => {
    if (agentsResponse) {
      setAgents([...InitialAgents, ...agentsResponse]);
    }
    if (groupsResponse) {
      setGroups([...InitialGroups, ...groupsResponse]);
    }
  }, [agentsResponse, groupsResponse]);

  useLoaderWhile(isLoadingAgents, isLoadingGroups);

  const { control, setValue } = useFormContext();

  return (
    <div className={'w-full flex flex-col gap-2.5 p-2.5 bg-[#A498FF1A] rounded-sm-5'}>
      {!isErrorAgents && !isErrorGroups ? (
        <div className={'flex flex-col gap-2.5'}>
          {agents.length > 0 && (
            <AgentsSelect
              label={
                <div className={'flex flex-row gap-2 items-center mb-1'}>
                  Freshdesk account for AI{' '}
                  <InputInfoToolTip
                    infoToolTip={{
                      side: 'right',
                      description: <>AI will use this account to handle tickets.</>,
                    }}
                  />
                </div>
              }
              name={'responder_id'}
              options={agents}
              control={control}
              setValue={setValue}
            />
          )}
          {groups.length > 0 && (
            <EscalationGroupSelect
              label={
                <div className={'flex flex-row gap-2 items-center mb-1'}>
                  Escalation group{' '}
                  <InputInfoToolTip
                    infoToolTip={{
                      side: 'right',
                      description: <>AI will assign escalated tickets to this group.</>,
                    }}
                  />
                </div>
              }
              control={control}
              name={'escalate_assign_group_id'}
              options={groups}
            />
          )}
        </div>
      ) : (
        <p>
          The Freshdesk details you entered seem invalid. Please double-check your subdomain, and
          API token following{' '}
          <a href={selectedIntegration.setupGuideUrl} target="_blank" rel="noreferrer">
            this guide
          </a>
          {'.'}
        </p>
      )}
    </div>
  );
};
